/**
 * useTourEngine
 *
 * Core state machine for the universal guided tour. Manages flow selection,
 * step index, objective tracking and minimized state.
 */

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  TourFlow,
  TourObjective,
  TourStep,
  TourProgressState,
  TourMascotConfig,
  TourAudioConfig,
} from '@/config/guided-tours/types';
import type { TourFormat } from '@/components/training/guided-tour/utils/formatResolver';
import { pickBestFormat } from '@/components/training/guided-tour/utils/formatResolver';

export type TourViewMode = 'index' | 'objective-index' | 'runner' | 'format-picker' | 'chat';

interface UseTourEngineOptions {
  flows: TourFlow[];
  objectives: TourObjective[];
  activeTab: string;
  initialFlowId?: string | null;
  initialStepIndex?: number;
  initialObjectiveId?: string | null;
  initialFormat?: TourFormat | null;
  onClose: () => void;
  onNavigate: (tab: string) => void;
  persistFlowActive?: (flowId: string, stepIndex: number, objectiveId?: string | null) => void;
}

export interface UseTourEngineResult {
  viewMode: TourViewMode;
  selectedFlowId: string | null;
  /** The flow whose section_prompt is used as AI chat context (null = module-level chat). */
  chatFlowId: string | null;
  selectedObjectiveId: string | null;
  currentStepIndex: number;
  currentStep: TourStep | null;
  currentSteps: TourStep[];
  selectedFlow: TourFlow | null;
  selectedObjective: TourObjective | null;
  selectedFlowObjectives: TourObjective[];
  isMinimized: boolean;
  isLastStep: boolean;
  isFirstStep: boolean;
  currentFormat: TourFormat;
  setCurrentFormat: (format: TourFormat) => void;
  selectFlow: (flowId: string, format?: TourFormat, stepId?: string) => void;
  selectChat: (flowId?: string) => void;
  selectObjective: (objectiveId: string) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStepById: (stepId: string) => void;
  returnToIndex: () => void;
  returnToIndexFromChat: () => void;
  showObjectiveIndex: () => void;
  minimize: () => void;
  restore: () => void;
  close: () => void;
  canAdvance: boolean;
  isObjectiveIndexMode: boolean;
  hasSelectedFlow: boolean;
  shouldShowFormatPicker: boolean;
  /**
   * Consume the "suppress DOM evidence" flag that is set on every step
   * transition. Returns true if DOM evidence should be ignored for the
   * current check (e.g. the target_selector from the previous step is still
   * visible in the DOM). The flag is reset to false after consumption so
   * subsequent genuine DOM changes are honoured.
   */
  consumeDomEvidenceSuppression: () => boolean;
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function useTourEngine({
  flows,
  objectives,
  activeTab,
  initialFlowId,
  initialStepIndex = 0,
  initialObjectiveId,
  initialFormat,
  onClose,
  onNavigate,
  persistFlowActive,
}: UseTourEngineOptions): UseTourEngineResult {
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(initialFlowId ?? null);
  const [chatFlowId, setChatFlowId] = useState<string | null>(null);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string | null>(
    initialObjectiveId ?? null
  );
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(initialStepIndex);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<TourFormat>(initialFormat ?? 'interactive');
  const [viewMode, setViewMode] = useState<TourViewMode>('index');
  const suppressDomEvidenceRef = useRef(false);

  const selectedFlow = useMemo(() => {
    if (!selectedFlowId) {
      return null;
    }
    return flows.find((flow) => flow.id === selectedFlowId) ?? null;
  }, [flows, selectedFlowId]);

  // Build a lookup map of all steps across all flows in the module so
  // objectives can reference steps by ID without duplicating them.
  const stepLookup = useMemo(() => {
    const map = new Map<string, TourStep>();
    for (const flow of flows) {
      for (const step of flow.steps ?? []) {
        if (step.id) {
          map.set(step.id, step);
        }
      }
    }
    return map;
  }, [flows]);

  const currentSteps = useMemo(() => {
    if (selectedObjectiveId) {
      const objective = objectives.find((obj) => obj.id === selectedObjectiveId);
      if (!objective?.step_ids) {
        return [];
      }
      return objective.step_ids
        .map((id) => stepLookup.get(id))
        .filter((step): step is TourStep => step != null);
    }

    return selectedFlow?.steps ?? [];
  }, [objectives, selectedFlow, selectedObjectiveId, stepLookup]);

  const currentStep = useMemo(() => {
    if (currentStepIndex < 0 || currentStepIndex >= currentSteps.length) {
      return null;
    }
    return currentSteps[currentStepIndex];
  }, [currentStepIndex, currentSteps]);

  const selectedObjective = useMemo(() => {
    if (!selectedObjectiveId) {
      return null;
    }
    return objectives.find((obj) => obj.id === selectedObjectiveId) ?? null;
  }, [objectives, selectedObjectiveId]);

  const selectedFlowObjectives = useMemo(() => {
    if (!selectedFlow) {
      return [];
    }

    return objectives.filter(
      (obj) =>
        obj.target_tab === selectedFlow.target_tab ||
        (selectedFlow.nav_items?.includes(obj.target_tab) ?? false)
    );
  }, [objectives, selectedFlow]);

  const isLastStep = currentSteps.length > 0 && currentStepIndex === currentSteps.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const hasSelectedFlow = !!selectedFlowId;
  const isObjectiveIndexMode = viewMode === 'objective-index';

  const shouldShowFormatPicker = useMemo(() => {
    return !!selectedFlow && currentFormat !== 'interactive';
  }, [currentFormat, selectedFlow]);

  // Persist progress when the active flow / step changes.
  useEffect(() => {
    if (selectedFlowId && persistFlowActive) {
      persistFlowActive(selectedFlowId, currentStepIndex, selectedObjectiveId);
    }
  }, [selectedFlowId, currentStepIndex, selectedObjectiveId, persistFlowActive]);

  const selectFlow = useCallback(
    (flowId: string, format?: TourFormat, stepId?: string) => {
      const flow = flows.find((f) => f.id === flowId);
      if (!flow) {
        return;
      }

      let stepIndex = 0;
      if (stepId) {
        const foundIndex = flow.steps.findIndex((step) => step.id === stepId);
        if (foundIndex >= 0) {
          stepIndex = foundIndex;
        }
      }

      suppressDomEvidenceRef.current = true;
      setSelectedFlowId(flowId);
      setSelectedObjectiveId(null);
      setCurrentStepIndex(stepIndex);
      // Opening a flow is an explicit "run the tour" — always surface the
      // runner even if the tour bubble was minimized.
      setIsMinimized(false);
      setViewMode('runner');

      const bestFormat = format ?? pickBestFormat(flow, currentFormat);
      setCurrentFormat(bestFormat);

      if (flow.target_tab && activeTab !== flow.target_tab) {
        onNavigate(flow.target_tab);
      }
    },
    [activeTab, currentFormat, flows, onNavigate]
  );

  const selectChat = useCallback(
    (flowId?: string) => {
      // If a flowId is provided, set it as the chat context and navigate
      // to its target_tab so the chat has the right page context.
      if (flowId) {
        const flow = flows.find((f) => f.id === flowId);
        if (flow) {
          setChatFlowId(flowId);
          setSelectedFlowId(flowId);
          if (flow.target_tab && activeTab !== flow.target_tab) {
            onNavigate(flow.target_tab);
          }
        }
      } else {
        // Module-level chat — no specific flow context
        setChatFlowId(null);
      }
      suppressDomEvidenceRef.current = true;
      setViewMode('chat');
    },
    [activeTab, flows, onNavigate]
  );

  const returnToIndexFromChat = useCallback(() => {
    suppressDomEvidenceRef.current = true;
    setChatFlowId(null);
    setSelectedFlowId(null);
    setSelectedObjectiveId(null);
    setCurrentStepIndex(0);
    setViewMode('index');
  }, []);

  const selectObjective = useCallback(
    (objectiveId: string) => {
      const objective = objectives.find((obj) => obj.id === objectiveId);
      if (!objective) {
        return;
      }

      suppressDomEvidenceRef.current = true;
      setSelectedObjectiveId(objectiveId);
      setCurrentStepIndex(0);
      setViewMode('runner');

      if (objective.target_tab && objective.target_tab !== activeTab) {
        onNavigate(objective.target_tab);
      }
    },
    [activeTab, objectives, onNavigate]
  );

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < currentSteps.length - 1) {
      suppressDomEvidenceRef.current = true;
      const nextStep = currentSteps[currentStepIndex + 1];
      setCurrentStepIndex((index) => index + 1);
      if (nextStep?.target_tab && nextStep.target_tab !== activeTab) {
        onNavigate(nextStep.target_tab);
      }
      return;
    }

    // End of flow/objective.
    if (selectedObjectiveId) {
      const currentIndex = selectedFlowObjectives.findIndex(
        (obj) => obj.id === selectedObjectiveId
      );
      const nextObjective = selectedFlowObjectives[currentIndex + 1];
      if (nextObjective) {
        suppressDomEvidenceRef.current = true;
        setSelectedObjectiveId(nextObjective.id);
        setCurrentStepIndex(0);
        if (nextObjective.target_tab && nextObjective.target_tab !== activeTab) {
          onNavigate(nextObjective.target_tab);
        }
        return;
      }

      // No more objectives in this group — return to the index instead of
      // leaving the runner visible with no flow or objective selected.
      setSelectedObjectiveId(null);
      setSelectedFlowId(null);
      setCurrentStepIndex(0);
      setViewMode('index');
      return;
    }

    const currentFlowIndex = flows.findIndex((flow) => flow.id === selectedFlowId);
    const nextFlow = flows[currentFlowIndex + 1];
    if (nextFlow) {
      setSelectedFlowId(nextFlow.id);
      setCurrentStepIndex(0);
    } else {
      onClose();
    }
  }, [
    activeTab,
    currentStepIndex,
    currentSteps,
    flows,
    onClose,
    onNavigate,
    selectedFlowId,
    selectedFlowObjectives,
    selectedObjectiveId,
  ]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      suppressDomEvidenceRef.current = true;
      const prevStep = currentSteps[currentStepIndex - 1];
      setCurrentStepIndex((index) => index - 1);
      if (prevStep?.target_tab && prevStep.target_tab !== activeTab) {
        onNavigate(prevStep.target_tab);
      }
      return;
    }

    if (selectedObjectiveId) {
      const currentIndex = selectedFlowObjectives.findIndex(
        (obj) => obj.id === selectedObjectiveId
      );
      const prevObjective = selectedFlowObjectives[currentIndex - 1];
      if (prevObjective) {
        const prevObjectiveStepCount = prevObjective.step_ids?.length ?? 0;
        suppressDomEvidenceRef.current = true;
        setSelectedObjectiveId(prevObjective.id);
        setCurrentStepIndex(Math.max(0, prevObjectiveStepCount - 1));
        if (prevObjective.target_tab && prevObjective.target_tab !== activeTab) {
          onNavigate(prevObjective.target_tab);
        }
        return;
      }

      // No previous objective — return to the index.
      setSelectedObjectiveId(null);
      setSelectedFlowId(null);
      setCurrentStepIndex(0);
      setViewMode('index');
      return;
    }

    const currentFlowIndex = flows.findIndex((flow) => flow.id === selectedFlowId);
    const prevFlow = flows[currentFlowIndex - 1];
    if (prevFlow) {
      setSelectedFlowId(prevFlow.id);
      setCurrentStepIndex(prevFlow.steps.length - 1);
    }
  }, [activeTab, currentStepIndex, currentSteps, flows, onNavigate, selectedFlowId, selectedFlowObjectives, selectedObjectiveId]);

  const goToStepById = useCallback(
    (stepId: string) => {
      const steps = currentSteps;
      const index = steps.findIndex((step) => step.id === stepId);
      if (index >= 0) {
        suppressDomEvidenceRef.current = true;
        const step = steps[index];
        setCurrentStepIndex(index);
        if (step?.target_tab && step.target_tab !== activeTab) {
          onNavigate(step.target_tab);
        }
      }
    },
    [activeTab, currentSteps, onNavigate]
  );

  const returnToIndex = useCallback(() => {
    suppressDomEvidenceRef.current = true;
    setSelectedFlowId(null);
    setSelectedObjectiveId(null);
    setCurrentStepIndex(0);
    setViewMode('index');
  }, []);

  const showObjectiveIndex = useCallback(() => {
    suppressDomEvidenceRef.current = true;
    setSelectedFlowId(null);
    setSelectedObjectiveId(null);
    setCurrentStepIndex(0);
    setViewMode('objective-index');
  }, []);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const restore = useCallback(() => {
    suppressDomEvidenceRef.current = true;
    setIsMinimized(false);
  }, []);

  const close = useCallback(() => {
    setSelectedFlowId(null);
    setSelectedObjectiveId(null);
    setCurrentStepIndex(0);
    setIsMinimized(false);
    setViewMode('index');
    onClose();
  }, [onClose]);

  const canAdvance = true; // Advancement gating is handled by verification hooks.

  const consumeDomEvidenceSuppression = useCallback(() => {
    if (suppressDomEvidenceRef.current) {
      suppressDomEvidenceRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    viewMode,
    selectedFlowId,
    chatFlowId,
    selectedObjectiveId,
    currentStepIndex,
    currentStep,
    currentSteps,
    selectedFlow,
    selectedObjective,
    selectedFlowObjectives,
    isMinimized,
    isLastStep,
    isFirstStep,
    currentFormat,
    setCurrentFormat,
    selectFlow,
    selectChat,
    selectObjective,
    goToNextStep,
    goToPreviousStep,
    goToStepById,
    returnToIndex,
    returnToIndexFromChat,
    showObjectiveIndex,
    minimize,
    restore,
    close,
    canAdvance,
    isObjectiveIndexMode,
    hasSelectedFlow,
    shouldShowFormatPicker,
    consumeDomEvidenceSuppression,
  };
}
